import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Copy, Download, FileText } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { AuthLayout } from '../auth/AuthLayout';

/**
 * Комплект по ссылке (ПД-24): блок «Ссылка для получателя» на карточке исходящей накладной и открытая
 * страница получателя без учётной записи — состав, скачивание файлов, подтверждение получения.
 */

interface LinkView {
  id: string;
  recipientName: string;
  expiresAt: string;
  revoked: boolean;
  opens: number;
  downloads: number;
  lastOpenedAt: string | null;
  receivedAt: string | null;
  receivedName: string | null;
  createdBy: string | null;
  createdAt: string;
}

export function TransmittalLinks({ transmittalId, sent }: { transmittalId: string; sent: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['psd-transmittal-links', transmittalId],
    queryFn: () => api<LinkView[]>(`/psd/transmittals/${transmittalId}/links`),
    retry: false,
  });
  const [create, setCreate] = useState(false);
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/psd/transmittal-links/${id}/revoke`, { body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['psd-transmittal-links', transmittalId] });
      toast('Ссылка отозвана');
    },
  });
  if (q.isError) return null;
  const now = Date.now();
  return (
    <Panel
      title="Ссылка для получателя"
      actions={
        sent ? (
          <Button className="h-11 md:h-9" onClick={() => setCreate(true)}>
            Новая ссылка
          </Button>
        ) : null
      }
    >
      {!sent ? (
        <p className="text-[14px] text-ink-2">
          Ссылка делается после отправки накладной — состав уже не меняется.
        </p>
      ) : q.data?.length ? (
        <ul className="flex flex-col gap-3 text-[14px]">
          {q.data.map((l) => {
            const live = !l.revoked && new Date(l.expiresAt).getTime() > now;
            return (
              <li key={l.id} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium [overflow-wrap:anywhere]">{l.recipientName}</span>
                  {l.receivedAt ? (
                    <Badge tone="success">получено</Badge>
                  ) : live ? (
                    <Badge tone="info">до {date(l.expiresAt)}</Badge>
                  ) : (
                    <Badge>{l.revoked ? 'отозвана' : 'срок истёк'}</Badge>
                  )}
                </div>
                <span className="text-[13px] text-ink-2">
                  открыта {l.opens} раз, скачано файлов {l.downloads}
                  {l.lastOpenedAt ? ` · последний раз ${dateTime(l.lastOpenedAt)}` : ''}
                  {l.receivedAt ? ` · получил ${l.receivedName}, ${dateTime(l.receivedAt)}` : ''}
                </span>
                {live ? (
                  <div>
                    <Button
                      variant="ghost"
                      className="h-11 md:h-9"
                      loading={revoke.isPending && revoke.variables === l.id}
                      onClick={() => revoke.mutate(l.id)}
                    >
                      Отозвать
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[14px] text-ink-2">
          Заказчик, экспертиза или подрядчик без учётной записи скачают комплект по ссылке; открытия,
          скачивания и получение запишутся.
        </p>
      )}
      <ErrorBox error={revoke.error} />
      {create ? <NewLinkDialog transmittalId={transmittalId} onClose={() => setCreate(false)} /> : null}
    </Panel>
  );
}

function NewLinkDialog({ transmittalId, onClose }: { transmittalId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [days, setDays] = useState(14);
  const [made, setMade] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api<{ path: string }>(`/psd/transmittals/${transmittalId}/links`, {
        body: { recipientName: name.trim(), days },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['psd-transmittal-links', transmittalId] });
      setMade(`${window.location.origin}${r.path}`);
    },
  });
  const copy = async () => {
    if (!made) return;
    try {
      await navigator.clipboard.writeText(made);
      toast('Ссылка скопирована');
    } catch {
      toast('Скопируйте ссылку вручную');
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Ссылка для получателя"
      description={
        made
          ? 'Ссылка показывается один раз — скопируйте и отправьте получателю. Потерянную ссылку отзовите и сделайте новую.'
          : 'Получатель увидит состав накладной и скачает файлы без учётной записи.'
      }
      footer={
        made ? (
          <Button variant="primary" className="h-11 md:h-10" onClick={onClose}>
            Готово
          </Button>
        ) : (
          <>
            <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
              Отмена
            </Button>
            <Button
              variant="primary"
              className="h-11 md:h-10"
              loading={m.isPending}
              disabled={name.trim().length < 2}
              onClick={() => m.mutate()}
            >
              Сделать ссылку
            </Button>
          </>
        )
      }
    >
      {made ? (
        <div className="flex flex-col gap-2">
          <Input
            readOnly
            className="h-11 md:h-10"
            value={made}
            onFocus={(e) => e.target.select()}
            aria-label="Ссылка"
          />
          <div>
            <Button className="h-11 md:h-10" icon={<Copy className="size-4" />} onClick={copy}>
              Скопировать
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Кому"
            className="sm:col-span-2"
            hint="Организация или человек — попадёт в водяной знак"
          >
            <Input
              className="h-11 md:h-10"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ГУ «Управление строительства», Иванов И."
            />
          </Field>
          <Field label="Срок действия">
            <Select className="h-11 md:h-10" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[3, 7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d} дней
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}

interface Shared {
  company: string;
  projectCode: string;
  projectName: string;
  number: string;
  date: string;
  recipientName: string;
  expiresAt: string;
  receivedAt: string | null;
  receivedName: string | null;
  sets: Array<{
    setCode: string;
    setName: string;
    revision: string;
    annulled: boolean;
    files: Array<{ id: string; name: string; size: number }>;
  }>;
}

const size = (n: number) =>
  n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(n / 1024))} КБ`;

/** Страница получателя без учётной записи: /share/psd/$token */
export function SharePage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['psd-share', token],
    queryFn: () => api<Shared>(`/public/psd/${encodeURIComponent(token)}`),
    retry: false,
  });
  const [name, setName] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api(`/public/psd/${encodeURIComponent(token)}/receive`, { body: { name: name.trim() } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['psd-share', token] });
      toast('Получение подтверждено');
    },
  });
  if (q.isPending)
    return (
      <AuthLayout title="Проектная документация">
        <Spinner />
      </AuthLayout>
    );
  if (q.isError || !q.data)
    return (
      <AuthLayout title="Проектная документация">
        <EmptyState title="Ссылка не работает">
          Срок ссылки истёк, её отозвали или в адресе ошибка. Запросите новую у отправителя.
        </EmptyState>
      </AuthLayout>
    );
  const s = q.data;
  return (
    <AuthLayout title={`Накладная ${s.number}`}>
      <div className="flex flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          {s.company} передаёт проектную документацию по проекту {s.projectCode} «{s.projectName}» от{' '}
          {date(s.date)}. Получатель: {s.recipientName.replace(/\.+$/, '')}. Ссылка действует до{' '}
          {date(s.expiresAt)}.
        </p>
        <ul className="flex flex-col gap-3">
          {s.sets.map((set) => (
            <li key={`${set.setCode}-${set.revision}`} className="rounded-lg border border-line p-3">
              <div className="font-medium [overflow-wrap:anywhere]">
                {set.setCode}, ревизия {set.revision}
              </div>
              <div className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">{set.setName}</div>
              {set.annulled ? (
                <p className="mt-2 rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
                  Ревизия погашена — по ней не строят. Запросите у отправителя действующую.
                </p>
              ) : null}
              <ul className="mt-2 flex flex-col">
                {(set.annulled ? [] : set.files).map((f) => (
                  <li key={f.id}>
                    <a
                      href={`/api/v1/public/psd/${encodeURIComponent(token)}/files/${f.id}`}
                      className="flex min-h-11 items-center gap-2 text-ink underline-offset-4 hover:underline"
                    >
                      <FileText className="size-4 shrink-0 text-ink-3" />
                      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{f.name}</span>
                      <span className="shrink-0 text-[12px] text-ink-3">{size(f.size)}</span>
                      <Download className="size-4 shrink-0" />
                    </a>
                  </li>
                ))}
                {!set.files.length ? (
                  <li className="text-[13px] text-ink-3">Файлов нет — только бумажный экземпляр.</li>
                ) : null}
              </ul>
            </li>
          ))}
        </ul>
        {s.receivedAt ? (
          <p className="rounded-md bg-success-soft px-3 py-2 text-success">
            Получение подтвердил {s.receivedName}, {dateTime(s.receivedAt)}.
          </p>
        ) : (
          <div className="flex flex-col gap-2 rounded-lg border border-line p-3">
            <Field label="Подтвердить получение" hint="ФИО и должность получателя">
              <Input className="h-11" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Button
              variant="primary"
              className="h-11"
              loading={m.isPending}
              disabled={name.trim().length < 2}
              onClick={() => m.mutate()}
            >
              Документацию получил
            </Button>
            <ErrorBox error={m.error} />
          </div>
        )}
        <p className="text-[12px] text-ink-3">
          Скачанные листы PDF получают водяной знак с номером накладной и получателем. Открытия и скачивания
          записываются у отправителя.
        </p>
      </div>
    </AuthLayout>
  );
}
